import { Link as RouterLink, generatePath } from 'react-router';

import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { Button } from '@/ui/button-2.tsx';

import Breadcrumbs from '../../components/Breadcrumbs/index.ts';
import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_REPAYMENT_PERIOD } from './RRS.ts';

type RepaymentTableRow = {
  title: string;
  values: string[];
};

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

const rowClassName = 'mx-auto flex max-w-[900px]! flex-col sm:w-full sm:flex-row';

/** Scroll: self-stretch + min-w-0; **`max-w-[900px]!`** matches article rows and beats Section `*:max-w-[1200px]`. */
const repaymentTableWrapClassName =
  'relative mx-auto mb-12 w-full max-w-[900px]! min-w-0 self-stretch overflow-x-auto rounded-lg border border-[#CEE2FF] shadow-[0px_2px_4px_rgba(0,0,0,0.02)] touch-pan-x';

const headCellCorner =
  'typography-v1-legacy-table-cell sticky left-0 z-10 w-[130px] min-w-[130px] border border-[#CEE2FF] bg-white px-4! py-2! align-top text-right font-normal text-v1-background-200 sm:min-w-[250px]';

const headCellNumeric =
  'typography-v1-legacy-table-cell border border-[#CEE2FF] px-4! py-2! align-top text-right font-normal text-v1-background-200';

const bodyRowHeadCellClassName =
  'typography-v1-legacy-table-cell sticky left-0 z-10 w-[130px] min-w-[130px] border border-[#CEE2FF] bg-white px-4! py-2! align-top text-left font-normal text-v1-background-200 sm:min-w-[250px]';

const bodyValueCellClassName =
  'typography-v1-legacy-table-cell border border-[#CEE2FF] px-4! py-2! align-top text-right font-normal text-[#18A0FB]';

const tablesTopRow = ['', '1 år', '2 år', '3 år', '4 år', '5 år'];

const table1Rows = [
  {
    title: 'Betaling per måned',
    values: ['9.263 kr', '5.090 kr', '3.716 kr', '3.043 kr', '2.649 kr'],
  },
  {
    title: 'Du betaler til sammen',
    values: ['111.156 kr', '122.160 kr', '133.776 kr', '146.064 kr', '158.940 kr'],
  },
  {
    title: 'Du sparer til sammen',
    values: ['47.784 kr', '36.780 kr', '25.164 kr', '12.876 kr', '0 kr'],
  },
];

const table2Rows = [
  {
    title: 'Betaling per måned',
    values: ['8.792 kr', '4.167 kr', '3.227 kr', '2.536 kr', '2.125 kr'],
  },
  {
    title: 'Du betaler til sammen',
    values: ['105.504 kr', '110.736 kr', '116.172 kr', '121.728 kr', '127.500 kr'],
  },
  {
    title: 'Du sparer til sammen',
    values: ['21.996 kr', '16.764 kr', '11.328 kr', '5.772 kr', '0 kr'],
  },
];

const RepaymentPeriod: React.FC = () => {
  const { appStrings, routes } = LANG;
  const { toggleChat } = useBrainSession();

  const handleDebtCheckClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  const renderTable = (rows: RepaymentTableRow[]) => (
    <div className={repaymentTableWrapClassName}>
      <table className="w-full min-w-[690px] border-collapse border-hidden">
        <thead>
          <tr>
            {tablesTopRow.map((column, index) => {
              const isFirstColumn = index === 0;
              return (
                <th
                  key={`h-${index}`}
                  scope="col"
                  className={isFirstColumn ? headCellCorner : headCellNumeric}
                >
                  {isFirstColumn ? (
                    <span
                      className="absolute top-[-8px] left-[130px] h-[243px] w-px bg-[#CEE2FF] min-[680px]:hidden sm:left-[250px] sm:h-[170px]"
                      aria-hidden
                    />
                  ) : null}
                  {column}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ title, values }) => (
            <tr key={title}>
              <th scope="row" className={bodyRowHeadCellClassName}>
                {title}
              </th>
              {values.map((value) => (
                <td key={value} className={bodyValueCellClassName}>
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Page title={appStrings.MENU.REPAYMENT_PERIOD}>
      <RrsHandler {...RRS_REPAYMENT_PERIOD} />
      <Section>
        <div className="flex max-w-[900px]! flex-col [&>button]:self-center">
          <Breadcrumbs
            className="mb-8"
            items={[
              { text: appStrings.MENU.HOME, url: routes.HOME },
              {
                text: appStrings.MENU.MY_ECONOMY,
                url: routes.MY_ECONOMY,
              },
              { text: appStrings.MENU.REPAYMENT_PERIOD },
            ]}
          />
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Nedbetalingstid – jo kortere, desto bedre
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Nedbetalingstiden er enkelt forklart den tiden det tar deg å betale tilbake et lån. Men
            hva skjer med lånet dersom du har mulighet til å betale ned raskere? Vi kan røpe såpass
            at om du velger kortere nedbetalingstid kan du spare MYE penger.
          </h2>
          <p className="mb-8 typography-v1-legacy-subtitle2 sm:mb-14">
            Når du tar opp et lån, har det alltid en nedbetalingstid. Avhengig av type varierer
            denne fra lang til kort – på boliglån kan den være opptil 30 år, på studielån 20 år, på
            forbrukslån inntil 5 år, mens på kredittkort har du som regel kun 45 dager på deg.
            <br />
            <br />
            Noe som er typisk er at jo kortere nedbetalingstiden på lånet er, desto høyere er
            renten. Her spiller ofte sikkerhet inn også – kan du stille pant i en bolig eller bil
            får du lavere rente enn om du har et forbrukslån uten sikkerhet. Har du flere lån er det
            alltid smart å prioritere å betale ned det med høyest rente først, og så jobbe deg
            nedover.
          </p>
          <Button color="yellow" onClick={handleDebtCheckClick}>
            Bli raskere gjeldfri!
          </Button>
        </div>
      </Section>
      <Section background="paper" last>
        <div className={rowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Spar mye med kortere nedbetalingstid
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Felles for alle lån er at dersom du betaler ned fortere vil du spare penger, selv om
              det ved første øyekast kanskje ikke virker slik siden du betaler tilbake mer per
              måned. Men hvorfor bli månedsbeløpet lavere med lengre nedbetalingstid, og
              totalbeløpet lavere med kortere nedbetalingstid?
              <br />
              <br />
              Dette skyldes enkelt og greit at du betaler rente. Jo høyere den er, desto mer er det
              å spare. 20 prosent rente betyr at du betaler 1/5 av lånet ditt bare i renter – hvert
              år. I tillegg må du betale flere gebyrer, og det er derfor det baller på seg. Dermed
              lønner det seg alltid å nedbetale et lån så raskt du kan.
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              La oss vise dette med et eksempel der du har et lån på 100.000 kroner med nominell
              rente på 20 prosent over 1 til 5 år. I tabellen har vi ikke tatt hensyn til effektiv
              rente som inkluderer etableringsgebyr, termingebyr og andre kostnader du må betale i
              tillegg – hvilket gjør at du med kortere nedbetalingstid i virkeligheten vil spare
              enda mer.
              <br />
              <br />
              Som du ser kan du spare mye på å velge kortere nedbetalingstid, faktisk nærmere 50.000
              kroner. Og tenk om lånesummen er høyere enn 100.000 kroner – da vil du spare enda mer.
              <br />
              <br />
              Men så er det jo gjerne slik at økonomien din gjør det vanskelig å betale raskere ned
              på lånet. Eller finnes det en måte du faktisk kan få det til på?
            </p>
          </div>
        </div>

        {renderTable(table1Rows)}

        <div className={rowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Spar mer med refinansiering</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Med refinansiering kan tallene nemlig se enda bedre ut. For la oss si at du får
              søknaden din innvilget, og halverer renten på de 100.000 kronene til 10 prosent – hva
              skjer da?
            </p>
          </div>
          <div className="w-full sm:w-1/2 sm:pl-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Her ser du at månedsbeløpene og det du betaler til sammen går kraftig ned fra 20 til
              10 prosent. Og de pengene kan du jo bruke til betale ned lånet enda raskere?
            </p>
          </div>
        </div>

        {renderTable(table2Rows)}

        <div className={rowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Refinansiering hos Scoopr – det tar deg to minutter å søke
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Hos sammenligningstjenesten Scoopr kan du søke om{' '}
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                refinansiering fra 20.000 til 500.000 kroner
              </RouterLink>
              . Har du BankID klar, kan du gjøre det nå – det er enkelt, gratis og uforpliktende.
              <br />
              <br />
              Når du har fylt ut{' '}
              <RouterLink
                to={generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                  articleName: 'hvordan-soker-jeg-pa-refinansiering-hos-scoopr',
                })}
                className={linkClassName}
              >
                skjemaet på denne siden
              </RouterLink>
              , er jobben din gjort. Fra Scoopr går søknaden din automatisk til 13 banker, som alle
              konkurrerer om å få deg som kunde. Det betyr at de strekker seg langt for å gi deg de
              beste betingelsene – altså så lav rente som mulig. Hvilken rente du får avhenger av
              betalingsevnen og økonomien din, som blant annet formue, inntekt og om du har annen
              gjeld.
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <p className="typography-v1-legacy-p3">
              Dersom søknaden din blir innvilget sitter du snart med tilbud fra bankene, og så kan
              du avgjøre hvilket av dem som passer deg og økonomien din best. Et godt tips til slutt
              når du skal velge bank – se nøye på den effektive renten, og hvor mye du totalt må
              betale tilbake.
              <br />
              <br />
              <strong>Er du nysgjerrig på hvor mye du kan spare?</strong>{' '}
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                Da kan du søke om refinansiering her.
              </RouterLink>
            </p>
          </div>
        </div>

        <div className="mt-20 flex flex-col items-center [&>button]:self-center">
          <Button color="yellow" onClick={handleDebtCheckClick}>
            Bli raskere gjeldfri!
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default RepaymentPeriod;
